import { useEffect, useState } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { Input, Loader, Message, Table } from 'semantic-ui-react';
import { isUrl } from '@plone/volto/helpers/Url/Url';
import { Column, DataTableData } from './types';

const messages = defineMessages({
  fetchError: {
    id: 'Could not load data.',
    defaultMessage: 'Could not load data.',
  },
  labelSearch: {
    id: 'Search',
    defaultMessage: 'Search',
  },
});

interface Props {
  data: DataTableData;
  isEditMode: boolean;
}

const View: React.FC<Props> = ({ data, isEditMode }) => {
  const [error, setError] = useState<string>('');
  const [fetchedData, setFetchedData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchInput, setSearchInput] = useState<string>('');

  const intl = useIntl();

  useEffect(() => {
    setError('');
    setFetchedData([]);
    setIsLoading(true);

    if (isUrl(data.data_source)) {
      fetch(data.data_source)
        .then(async (res) => {
          try {
            const json = await res.json();
            if (Array.isArray(json)) setFetchedData(json);
            else if (typeof json === 'object')
              setFetchedData(Object.values(json));
          } catch (e) {
            let msg = intl.formatMessage(messages.fetchError);
            // only include detailed info in edit mode
            if (isEditMode) msg += `\n${e}`;
            setError(msg);
          } finally {
            setIsLoading(false);
          }
        })
        .catch((e) => {
          let msg = intl.formatMessage(messages.fetchError);
          // only include detailed info in edit mode
          if (isEditMode) msg += `\n${e}`;
          setError(msg);
        });
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [data.data_source]);

  const filteredData: any[] =
    searchInput.trim().length === 0
      ? fetchedData
      : (fetchedData || []).filter(
          (item: any) =>
            searchInput.length === 0 ||
            Object.values(item).find(
              (value: any) =>
                value
                  .toString()
                  .toLowerCase()
                  .indexOf(searchInput.toLowerCase()) > -1,
            ),
        );

  const columns: Column[] =
    data?.columns?.length > 0
      ? data.columns
      : filteredData && filteredData.length > 0
      ? Object.keys(filteredData[0]).map((key) => {
          return { '@id': key, label: key, field: key };
        })
      : [];

  if (columns.length > 0 && data.sort_key) {
    filteredData.sort((a, b) =>
      a[data.sort_key] > b[data.sort_key]
        ? data.sort_reverse === true
          ? -1
          : 1
        : data.sort_reverse === true
        ? 1
        : -1,
    );
  }

  return (
    <div className="data-table-block">
      {isLoading && (
        <Loader size="large" inline="centered" active>
          <FormattedMessage id="Loading" defaultMessage="Loading" /> ...
        </Loader>
      )}
      {error && <Message error>{error}</Message>}
      {!isLoading && fetchedData.length === 0 && (
        <Message warning>
          <FormattedMessage
            id="No results found."
            defaultMessage="No results found."
          />
        </Message>
      )}
      {fetchedData.length > 0 && columns.length > 0 && (
        <>
          {data.searchable && (
            <Input
              fluid
              placeholder={intl.formatMessage(messages.labelSearch)}
              type="text"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
            />
          )}
          {/* @ts-ignore */}
          <Table celled selectable>
            <Table.Header>
              <Table.Row>
                {columns.map((column) => (
                  <Table.HeaderCell key={column['@id']}>
                    {column.label}
                  </Table.HeaderCell>
                ))}
              </Table.Row>
            </Table.Header>
            {filteredData.map((item, index) => (
              <Table.Row key={`dt-row-${index}`}>
                {columns.map((column) => (
                  <Table.Cell key={`dt-row-${index}-cell-${column['@id']}`}>
                    {item.hasOwnProperty(column.field)
                      ? item[column.field]
                      : ''}
                  </Table.Cell>
                ))}
              </Table.Row>
            ))}
          </Table>
        </>
      )}
    </div>
  );
};

export default View;
